import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MARKET_API_BASE, SESSION_EXPIRED_EVENT, setAuthToken } from "../backendConfig";

const AUTH_API_BASE = MARKET_API_BASE;
const AUTH_TOKEN_KEY = "lwt.authToken";

function savedAuthToken() {
  try { return window.sessionStorage.getItem(AUTH_TOKEN_KEY) || null; }
  catch { return null; }
}
/** True when the page is opened from this computer or the home network (never the public site). Pure. */
export function isPrivateHost(hostname) {
  const h = String(hostname || "").toLowerCase();
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "[::1]" ||
    h === "::1" ||
    /^10\.\d+\.\d+\.\d+$/.test(h) ||
    /^192\.168\.\d+\.\d+$/.test(h) ||
    /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(h)
  );
}

/** Local-machine escape hatch: set REACT_APP_DISABLE_AUTH=true in .env.local (gitignored)
 * to skip sign-in entirely and land straight on the chart. It only works when the page is opened from this
 * computer or the home network, so a build that accidentally carries the flag still asks for sign-in on
 * the public site. */
const AUTH_DISABLED =
  process.env.REACT_APP_DISABLE_AUTH === "true" && typeof window !== "undefined" && isPrivateHost(window.location?.hostname);
const RECHECK_MS = 10 * 60 * 1000; // also re-check the login when the tab comes back into view
const LOCAL_USER = {
  id: "local",
  email: "local@localhost",
  display_name: "Local",
  created_at: null,
};

const AuthContext = createContext(null);

async function apiRequest(path, options = {}) {
  const res = await fetch(`${AUTH_API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export function AuthProvider({ children }) {
  useEffect(() => {
    try { window.localStorage.removeItem("lwt.authToken"); window.sessionStorage.removeItem("lwt.guestSession"); } catch { /* storage may be unavailable */ }
  }, []);

  const [token, setToken] = useState(() => {
    const initialToken = savedAuthToken();
    setAuthToken(initialToken);
    return initialToken;
  });
  const [user, setUser] = useState(AUTH_DISABLED ? LOCAL_USER : null);
  const [loading, setLoading] = useState(!AUTH_DISABLED);

  const persistToken = useCallback((nextToken) => {
    setToken(nextToken);
    setAuthToken(nextToken);
    try {
      if (nextToken) window.sessionStorage.setItem(AUTH_TOKEN_KEY, nextToken);
      else window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
    } catch { /* authentication continues for this page session */ }
  }, []);

  useEffect(() => {
    if (AUTH_DISABLED) return undefined;
    let cancelled = false;
    if (!token) { setUser(null); setLoading(false); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const me = await apiRequest("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) { persistToken(null); setUser(null); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [persistToken, token]);

  // The server stopped accepting this login (expired after a day, password reset, signed out elsewhere):
  // sign this tab out at once so protected pages send the visitor to /login instead of staying on screen.
  useEffect(() => {
    if (AUTH_DISABLED) return undefined;
    const signOut = () => {
      persistToken(null);
      setUser(null);
    };
    const recheck = async () => {
      const active = token;
      if (!active || document.visibilityState === "hidden") return;
      try {
        await apiRequest("/auth/me", { headers: { Authorization: `Bearer ${active}` } });
      } catch (err) {
        if (err.status === 401 || err.status === 403) signOut(); // a network blip is not a reason to sign out
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") recheck();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, signOut);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(recheck, RECHECK_MS);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, signOut);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [persistToken, token]);

  const signup = useCallback(
    async (email, password, displayName) => {
      const data = await apiRequest("/auth/signup", {
        method: "POST",
        body: JSON.stringify({ email, password, display_name: displayName }),
      });
      persistToken(data.access_token);
      setUser(data.user);
      return data.user;
    },
    [persistToken]
  );

  const login = useCallback(
    async (email, password) => {
      const data = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      persistToken(data.access_token);
      setUser(data.user);
      return data.user;
    },
    [persistToken]
  );

  const logout = useCallback(() => {
    persistToken(null);
    // With auth disabled locally, signing out would strand you on /login with no way
    // back in, so stay on the stub user instead.
    setUser(AUTH_DISABLED ? LOCAL_USER : null);
  }, [persistToken]);

  const requestPasswordReset = useCallback(
    (email) => apiRequest("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
    []
  );

  const resetPassword = useCallback(
    (resetToken, newPassword) =>
      apiRequest("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      }),
    []
  );

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      const data = await apiRequest("/auth/change-password", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      // The server signs every other session out, so keep this tab on the fresh token.
      persistToken(data.access_token);
      setUser(data.user);
      return data.user;
    },
    [token, persistToken]
  );

  const value = useMemo(
    () => ({ user, token, loading, signup, login, logout, requestPasswordReset, resetPassword, changePassword }),
    [user, token, loading, signup, login, logout, requestPasswordReset, resetPassword, changePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
